import React from 'react';

import FormTextBox, { FormTextBoxDataType } from 'src/components/controls/forms/redesign/FormTextbox';
import Form from 'src/components/controls/forms/Form';
import FormSelect from 'src/components/controls/forms/redesign/FormFilterSelect';
import FormToggle from 'src/components/controls/forms/redesign/FormToggle';
import { AnyObject, FormApi } from 'final-form';
import { HorizonInterval, Formation, HorizonPart } from 'src/models/Horizon';
import { Field } from 'react-final-form';
import PropertyUtil from 'src/util/PropertyUtil';
import { IDialogWizardAction } from 'src/components/common/DialogWizard';
import FormRow from 'src/components/controls/forms/FormRow';
import FormTextArea from 'src/components/controls/forms/redesign/FormTextArea';
import FormCheckbox from 'src/components/controls/forms/redesign/FormCheckbox';

interface IHorizonIntervalComponentProps {
    actions?: Array<IDialogWizardAction>;
    formations: Array<Formation>;
    showAddFormationPanel: () => void;
    horizonInterval?: HorizonInterval;
    updateHorizonInterval?: (horizonInterval: HorizonInterval) => void;
    onNext: (horizonInterval: HorizonInterval) => void;
    onPrev: () => void;
    onCancel?: () => void;
    onGoToFormationPanel?: () => void;
    isInDialog?: boolean;
}

interface IHorizonIntervalState {
    currentFromTab: number;
    currentToTab: number;
    fromToggleDisabled: boolean;
    toToggleDisabled: boolean;
}

class HorizonIntervalComponent extends React.Component<IHorizonIntervalComponentProps, IHorizonIntervalState> {
    private panels = ['Surface', 'Formation', 'Depth', 'Custom'];
    private initValues: AnyObject;
    private formRef: FormApi;

    constructor(props: IHorizonIntervalComponentProps) {
        super(props);

        this.state = {
            currentFromTab: 1,
            currentToTab: 1,
            fromToggleDisabled: true,
            toToggleDisabled: true,
        };
    }

    showAddFormationPanel = () => {
        this.props.showAddFormationPanel();
    };

    componentDidMount() {
        if (this.props.horizonInterval) {
            this.setState({
                currentFromTab: this.getTabIndexFromHorizonInterval(this.props.horizonInterval.from),
                currentToTab: this.getTabIndexFromHorizonInterval(this.props.horizonInterval.to),
            });
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: IHorizonIntervalComponentProps) {
        if (!nextProps.horizonInterval || this.props.horizonInterval !== nextProps.horizonInterval) {
            const updateForm = () => {
                try {
                    if (this.formRef) {
                        this.formRef.initialize(nextProps.horizonInterval);
                        this.onChange(nextProps.horizonInterval, this.formRef);
                        this.updateToggles(nextProps.horizonInterval.from.isSurface, nextProps.horizonInterval.to.isCenterEarth);
                    }
                } catch (e) {
                    console.log('error', e);
                }
            };

            // TODO: Nasty, state render race problem
            setTimeout(() => {
                if (this.props.horizonInterval) {
                    this.setState(
                        {
                            currentFromTab: this.getTabIndexFromHorizonInterval(this.props.horizonInterval.from),
                            currentToTab: this.getTabIndexFromHorizonInterval(this.props.horizonInterval.to),
                        },
                        updateForm
                    );
                } else {
                    try {
                        this.setState({ currentFromTab: 0, currentToTab: 0 }, updateForm);
                    } catch (e) {
                        console.log('state error');
                    }
                }
            }, 0);
        }
    }

    updateToggles = (isSurface: boolean, isCenterEarth: boolean) => {
        if (this.state.fromToggleDisabled !== isSurface || this.state.toToggleDisabled !== isCenterEarth) {
            this.setState({ fromToggleDisabled: isSurface, toToggleDisabled: isCenterEarth });
        }
    };

    getTabIndexFromHorizonInterval = (horizonPart: HorizonPart) => {
        let retVal = 0;

        if (horizonPart.isSurface || horizonPart.isCenterEarth) {
            retVal = 0;
        } else if (horizonPart.namedHorizon) {
            retVal = 1;
        } else if (horizonPart.depth) {
            retVal = 2;
        } else if (horizonPart.custom) {
            retVal = 3;
        }

        return retVal;
    };

    onChange = (state: AnyObject, form: FormApi) => {
        const def = HorizonInterval.from(state.values);

        const fromNamedHorizon = PropertyUtil.SafeGet(() => def.from.namedHorizon);
        const toNamedHorizon = PropertyUtil.SafeGet(() => def.to.namedHorizon);

        this.updateToggles(def.from.isSurface, def.to.isCenterEarth);
        if (def.from.isSurface) {
            form.change('fromPanel', 'Surface');
            form.change('from.type', 'extent');
        } else {
            switch (this.state.currentFromTab) {
                case 1: {
                    form.change('fromPanel', 'Formation');
                    form.change('from.type', 'formation');
                    break;
                }
                case 2: {
                    form.change('fromPanel', 'Depth');
                    form.change('from.type', 'depth');
                    break;
                }
                case 3: {
                    form.change('fromPanel', 'Custom');
                    form.change('from.type', 'custom');
                    break;
                }
                default: {
                    form.change('fromPanel', 'Formation');
                    form.change('from.type', 'formation');
                    this.setState({ currentFromTab: 1 });
                    break;
                }
            }
        }

        if (def.to.isCenterEarth) {
            form.change('toPanel', 'Surface');
            form.change('to.type', 'extent');
        } else {
            switch (this.state.currentToTab) {
                case 1: {
                    form.change('toPanel', 'Formation');
                    form.change('to.type', 'formation');
                    break;
                }
                case 2: {
                    form.change('toPanel', 'Depth');
                    form.change('to.type', 'depth');
                    break;
                }
                case 3: {
                    form.change('toPanel', 'Custom');
                    form.change('to.type', 'custom');
                    break;
                }
                default: {
                    form.change('toPanel', 'Formation');
                    form.change('to.type', 'formation');
                    this.setState({ currentToTab: 1 });
                    break;
                }
            }
        }

        if (fromNamedHorizon) {
            if (state.values.from.namedHorizon.aboveBelow) {
                form.change(
                    'from.namedHorizon.isAbove',
                    (state.values.from.namedHorizon.aboveBelow as string).toLowerCase() === 'above' ? true : false
                );
                form.change(
                    'from.namedHorizon.isBelow',
                    (state.values.from.namedHorizon.aboveBelow as string).toLowerCase() === 'below' ? true : false
                );
            } else {
                form.change('from.namedHorizon.isAbove', false);
                form.change('from.namedHorizon.isBelow', false);
            }

            if (state.values.from.namedHorizon.topBase) {
                form.change('from.namedHorizon.isTop', (state.values.from.namedHorizon.topBase as string).toLowerCase() === 'top' ? true : false);
                form.change('from.namedHorizon.isBase', (state.values.from.namedHorizon.topBase as string).toLowerCase() === 'base' ? true : false);
            } else {
                form.change('from.namedHorizon.isTop', false);
                form.change('from.namedHorizon.isBase', false);
            }
        }

        if (toNamedHorizon) {
            if (state.values.to.namedHorizon.aboveBelow) {
                form.change('to.namedHorizon.isAbove', (state.values.to.namedHorizon.aboveBelow as string).toLowerCase() === 'above' ? true : false);
                form.change('to.namedHorizon.isBelow', (state.values.to.namedHorizon.aboveBelow as string).toLowerCase() === 'below' ? true : false);
            } else {
                form.change('to.namedHorizon.isAbove', false);
                form.change('to.namedHorizon.isBelow', false);
            }

            if (state.values.to.namedHorizon.topBase) {
                form.change('to.namedHorizon.isTop', (state.values.to.namedHorizon.topBase as string).toLowerCase() === 'top' ? true : false);
                form.change('to.namedHorizon.isBase', (state.values.to.namedHorizon.topBase as string).toLowerCase() === 'base' ? true : false);
            } else {
                form.change('to.namedHorizon.isTop', false);
                form.change('to.namedHorizon.isBase', false);
            }
        }

        const fromForm = PropertyUtil.SafeGet(() => def.from.namedHorizon.formation);
        if (fromForm && fromForm.name && !fromForm.id) {
            delete def.from.namedHorizon.formation;
        }

        const toForm = PropertyUtil.SafeGet(() => def.to.namedHorizon.formation);
        if (toForm && toForm.name && !toForm.id) {
            delete def.to.namedHorizon.formation;
        }

        if (PropertyUtil.SafeGet(() => def.from.namedHorizon.formation)) {
            const item = this.props.formations.filter((hor: AnyObject) => hor.id === def.from.namedHorizon.formation.id)[0];
            if (item) {
                def.from.namedHorizon.formation.name = item.name;
            }
        }

        if (PropertyUtil.SafeGet(() => def.to.namedHorizon.formation)) {
            const item = this.props.formations.filter((hor: AnyObject) => hor.id === def.to.namedHorizon.formation.id)[0];
            if (item) {
                def.to.namedHorizon.formation.name = item.name;
            }
        }
        if (def) {
            form.change('horizonDefinition', def.toString());
        }
    };

    handleTabChange = (tabName: string, section: string) => {
        const form = (window as AnyObject).addHorizon as FormApi;
        form.change(section, undefined);
    };

    handleSubmit = (values: HorizonInterval) => {
        this.props.updateHorizonInterval(values);
    };

    handleNext = (hi: HorizonInterval) => {
        if (hi.from.type === 'formation') {
            hi.from.namedHorizon.formation.name = this.props.formations.filter((f: Formation) => f.id === hi.from.namedHorizon.formation.id)[0].name;
        }

        if (hi.to.type === 'formation') {
            hi.to.namedHorizon.formation.name = this.props.formations.filter((f: Formation) => f.id === hi.to.namedHorizon.formation.id)[0].name;
        }

        this.props.onNext(hi);
    };

    showButtons = () => {
        const style: React.CSSProperties = {
            cursor: 'pointer',
            background: '#6393C1',
            padding: '.5rem 1rem',
            color: 'white',
            display: 'inline-block',
            marginRight: this.props.isInDialog ? '-1rem' : '1rem',
        };

        if (this.props.isInDialog) {
            return (
                <FormRow align="right">
                    <span>
                        <FormCheckbox label="Target Horizon" name="isTargetHorizon" />
                    </span>
                    <span style={style} onClick={this.props.onPrev}>
                        Previous
                    </span>
                    <span style={style} onClick={() => this.handleNext(this.formRef.getState().values as HorizonInterval)}>
                        Next
                    </span>
                    <span style={{ ...style, marginRight: '1rem' }} onClick={this.props.onCancel}>
                        Cancel
                    </span>
                </FormRow>
            );
        } else {
            return (
                <FormRow align="right">
                    <span>
                        <FormCheckbox label="Target Horizon" name="isTargetHorizon" />
                    </span>
                    <span style={style} onClick={() => this.props.onNext(this.formRef.getState().values as HorizonInterval)}>
                        Submit
                    </span>
                </FormRow>
            );
        }
    };

    submitAction = () => {
        console.log('submit called');
        // const returnAction: Array<IDialogWizardAction> = this.props.actions ? this.props.actions.filter((action: IDialogWizardAction) => action.isFormSubmit === true) : undefined;

        // if (returnAction) {
        //     const action = returnAction[0];
        //     return (values: HorizonInterval) => {
        //         if (action.navigateTo === 'next') {
        //             action.nextPage();
        //             returnAction[0].action(values);
        //         }
        //         if (action.navigateTo === 'prev') {
        //             action.prevPage();
        //         }
        //     };
        // }

        // return this.handleSubmit;
    };

    updateTab = (tab: string, isTop: boolean) => {
        const leftExpr = isTop ? 'from.' : 'to.';

        let tabNumber = 0;
        switch (tab) {
            case 'Formation':
                tabNumber = 1;
                this.formRef.change(`${leftExpr}.type`, 'formation');
                break;
            case 'Depth':
                tabNumber = 2;
                this.formRef.change(`${leftExpr}.type`, 'depth');
                break;
            case 'Custom':
                tabNumber = 3;
                this.formRef.change(`${leftExpr}.type`, 'custom');
                break;
            default:
                tabNumber = 0;
                this.formRef.change(`${leftExpr}.type`, 'extent');
                break;
        }

        if (isTop) {
            this.setState({ currentFromTab: tabNumber });
        } else {
            this.setState({ currentToTab: tabNumber });
        }
    };

    DefinitionPanel = (isTop: boolean) => {
        const name = isTop ? 'from' : 'to';
        const part = isTop ? this.state.currentFromTab : this.state.currentToTab;

        let returnPanel: React.ReactElement;

        const aboveBelowItems = ['Above', 'Below'];

        const topBottomItems = ['Top', 'Base'];

        const addFormationAction = () => {
            if (this.props.isInDialog) {
                return {
                    action: this.props.onGoToFormationPanel,
                    text: 'Add Formation',
                };
            } else {
                return {
                    action: this.showAddFormationPanel,
                    text: 'Add Formation',
                };
            }
        };

        if ((isTop && !this.state.fromToggleDisabled) || (!isTop && !this.state.toToggleDisabled)) {
            switch (part) {
                case 0:
                    returnPanel = <></>;
                    break;
                case 1: {
                    returnPanel = (
                        <>
                            <FormRow>
                                <span className="add-formation-section">
                                    <FormTextBox
                                        tabIndex={4}
                                        type={FormTextBoxDataType.Int}
                                        name={`${name}.namedHorizon.depth`}
                                        label="Depth (ft.)"
                                        style={{ maxWidth: '12rem' }}
                                    />
                                    <FormToggle tabIndex={5} name={`${name}.namedHorizon.aboveBelow`} items={aboveBelowItems} allowBlank={true} />
                                    <span className="text-block">The</span>
                                    <FormToggle tabIndex={6} name={`${name}.namedHorizon.topBase`} items={topBottomItems} allowBlank={true} />
                                    <span
                                        className="text-block"
                                        style={{ display: 'flex', alignItems: 'baseline', position: 'relative', top: '-.8rem' }}
                                    >
                                        <span style={{ display: 'inline-block', paddingRight: '.5rem' }}>of the </span>
                                        <FormSelect
                                            tabIndex={3}
                                            beforeAction={addFormationAction()}
                                            label="Formation"
                                            name={`${name}.namedHorizon.formation.id`}
                                            items={this.props.formations}
                                        />
                                    </span>
                                </span>
                                <FormTextBox
                                    type={FormTextBoxDataType.Int}
                                    name={`${name}.namedHorizon.approximateDepth`}
                                    label="Approx. Depth (ft.)"
                                />
                            </FormRow>
                        </>
                    );
                    break;
                }
                case 2: {
                    returnPanel = (
                        <>
                            <FormTextBox type={FormTextBoxDataType.Int} tabIndex={7} name={`${name}.depth`} label="Depth (ft.)" />
                        </>
                    );
                    break;
                }
                case 3: {
                    returnPanel = (
                        <>
                            <div style={{ padding: '0', borderBottom: '1px solid #ccc' }}>
                                <FormTextArea label="Custom Horizon" tabIndex={8} name={`${name}.custom`} />
                            </div>
                        </>
                    );
                    break;
                }
                default:
                    returnPanel = <></>;
                    break;
            }
        }

        return <div className="add-horizon-tab-content">{returnPanel}</div>;
    };

    HorizonPartPanel = (isTop: boolean, tabState: string) => {
        const name = isTop ? 'from' : 'to';

        return (
            <div style={{ padding: '1rem', border: '1px solid #ccc', background: '#f1f1f1' }}>
                <FormCheckbox label={`Is ${isTop ? 'Surface' : 'Center of Earth'}`} name={`${name}.${isTop ? 'isSurface' : 'isCenterEarth'}`} />
                <div style={{ width: '50%' }}>
                    <span style={{ fontWeight: 'bold', paddingBottom: '.5rem' }}>Select Horizon Type: </span>
                    <span style={{ display: 'inline-block' }}>
                        <FormToggle
                            name={`${name}Panel`}
                            items={['Formation', 'Depth', 'Custom']}
                            onChange={(val) => this.updateTab(val, isTop)}
                            // allowBlank={true}
                            disabled={isTop ? this.state.fromToggleDisabled : this.state.toToggleDisabled}
                        />
                    </span>
                </div>
                {this.DefinitionPanel(isTop)}
            </div>
        );
    };

    public render() {
        let initValues = { ...this.initValues, from: { isSurface: true }, to: { isCenterEarth: true }, isTargetHorizon: false };

        if (this.props.horizonInterval) {
            initValues = {
                ...this.initValues,
                from: this.props.horizonInterval.from,
                to: this.props.horizonInterval.to,
                isTargetHorizon: this.props.horizonInterval.isTargetHorizon,
            };

            if (this.props.horizonInterval.from && this.props.horizonInterval.from.namedHorizon) {
                if (this.props.horizonInterval.from.namedHorizon.isAbove) {
                    (initValues.from as AnyObject).namedHorizon.aboveBelow = 'Above';
                } else if (this.props.horizonInterval.from.namedHorizon.isBelow) {
                    (initValues.from as AnyObject).namedHorizon.aboveBelow = 'Below';
                }
                if (this.props.horizonInterval.from.namedHorizon.isTop) {
                    (initValues.from as AnyObject).namedHorizon.topBase = 'Top';
                } else if (this.props.horizonInterval.from.namedHorizon.isBase) {
                    (initValues.from as AnyObject).namedHorizon.topBase = 'Base';
                }
            }

            if (this.props.horizonInterval.to && this.props.horizonInterval.to.namedHorizon) {
                if (this.props.horizonInterval.to.namedHorizon.isAbove) {
                    (initValues.to as AnyObject).namedHorizon.aboveBelow = 'Above';
                } else if (this.props.horizonInterval.to.namedHorizon.isBelow) {
                    (initValues.to as AnyObject).namedHorizon.aboveBelow = 'Below';
                }
                if (this.props.horizonInterval.to.namedHorizon.isTop) {
                    (initValues.to as AnyObject).namedHorizon.topBase = 'Top';
                } else if (this.props.horizonInterval.to.namedHorizon.isBase) {
                    (initValues.to as AnyObject).namedHorizon.topBase = 'Base';
                }
            }

            (initValues as AnyObject).fromPanel = this.panels[this.state.currentFromTab];
            (initValues as AnyObject).toPanel = this.panels[this.state.currentToTab];
        }

        initValues = Object.assign({}, initValues);
        if (this.formRef) {
            this.formRef.reset();
            this.formRef.initialize(initValues);
        }

        return (
            <Form
                globalId="addHorizon"
                onSubmit={this.submitAction}
                onChange={this.onChange}
                initialValues={initValues}
                onFormCreated={(form: FormApi) => (this.formRef = form)}
                keepDirty={true}
                style={{ width: '90rem' }}
            >
                <div style={{ display: 'flex', flexDirection: 'column' }} className="horizon-interval-page">
                    <div>
                        <Field name="horizonDefinition">
                            {({ input }) => <span className="horizon-definition">{input.value || 'Invalid horizon definition'}</span>}
                        </Field>
                    </div>
                    <div style={{ display: 'inline-block', flexGrow: 1 }}>
                        <div style={{ fontWeight: 'bold', padding: '.5rem 0' }}>Top of Horizon Definition:</div>
                        {this.HorizonPartPanel(
                            true,
                            PropertyUtil.SafeGet(() => this.formRef.getFieldState('fromPanel').value, 'Formation')
                        )}
                    </div>
                    <div style={{ display: 'inline-block', flexGrow: 1 }}>
                        <div style={{ fontWeight: 'bold', padding: '.5rem 0' }}>Bottom of Horizon Definition</div>
                        {this.HorizonPartPanel(
                            false,
                            PropertyUtil.SafeGet(() => this.formRef.getFieldState('toPanel').value, 'Formation')
                        )}
                    </div>
                </div>
                <div className="dlc-form-action-bar">{this.showButtons()}</div>
            </Form>
        );
    }
}

export default HorizonIntervalComponent;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/AddHorizonComponents/HorizonIntervalComponent.tsx